import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { DocumentClient, DocumentProject } from '@/features/quotes/queries'
import { formatDateShort } from '@/lib/dates'
import { jobName } from '@/lib/jobs'

/**
 * The frame around a quote or invoice (29 Oct 2026), after Pixieset's: a back
 * arrow, the document's name and status, the project (or "No project"), the
 * save state, and the actions on the right; the client under it.
 */

export function projectLabel(job: DocumentProject | null): string {
  if (!job) return 'No project'
  return `${jobName(job)} · ${formatDateShort(job.event_date)}`
}

export function DocumentTopBar({
  back,
  title,
  status,
  project,
  projectTo,
  save,
  actions,
}: {
  back: string
  title: string
  status: ReactNode
  project: string
  projectTo: string | null
  save: ReactNode
  actions: ReactNode
}) {
  return (
    <header className="mb-4 flex flex-wrap items-start justify-between gap-x-6 gap-y-3 pb-2">
      <div className="flex min-w-0 items-start gap-3">
        <Link to={back} aria-label="Back" className="mt-1 rounded-md px-1.5 text-[length:var(--text-lg)] text-taupe hover:bg-[var(--color-wash-strong)] hover:text-ink">
          ←
        </Link>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[length:var(--text-2xl)] font-bold leading-none">{title}</h1>
            {status}
            {save}
          </div>
          <p className="mt-1.5 text-[length:var(--text-sm)] text-taupe">
            {projectTo ? (
              <Link to={projectTo} className="underline-offset-4 hover:underline">
                {project}
              </Link>
            ) : (
              project
            )}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
    </header>
  )
}

export function SaveState({ saving, savedAt, failed }: { saving: boolean; savedAt: Date | null; failed: boolean }) {
  return (
    <span className="text-[length:var(--text-2xs)] text-taupe">
      {saving ? 'Saving…' : failed ? <span className="text-clay">Not saved: check your connection</span> : savedAt ? 'All changes saved' : ''}
    </span>
  )
}

export function ClientCard({ client }: { client: DocumentClient | null }) {
  if (!client) return <p className="text-[length:var(--text-sm)] text-taupe">No client</p>
  const name = [client.first_name, client.last_name].filter(Boolean).join(' ')
  const initials = `${client.first_name.charAt(0)}${client.last_name?.charAt(0) ?? ''}`.toUpperCase()
  return (
    <div className="flex items-center gap-3">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-wash-strong)] text-[length:var(--text-xs)] font-semibold">
        {initials}
      </span>
      <div className="min-w-0">
        <p className="text-[length:var(--text-sm)] font-semibold">{name}</p>
        <p className="truncate text-[length:var(--text-2xs)] text-taupe">{[client.email, client.phone].filter(Boolean).join(' · ') || 'No email or phone'}</p>
      </div>
    </div>
  )
}
